import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  TextField,
} from "@mui/material";

import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import coin from "../../assets/coin_only.png";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DehazeIcon from "@mui/icons-material/Dehaze";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../assets/logo.svg";
import { useTheme } from "@emotion/react";
import "./styles.scss";
import Login from "../auth/signup";
import ZoneHoverComponent from "../hover/first";
import EventHoverComponent from "../hover/second";
import CorporateHoverComponent from "../hover/third";
import EmployeeManagementForms from "../employee/manage-section";
import DataForms from "../data-entry/entry";
import Packages from "../package/package";
import { setLoggedIn, removeUser } from "../../utils/store/slice/userSlice";
import MoneyBg from "../../assets/money-logo-bg.svg";
import {
  getProfile,
  isAdmin,
  isEmployee,
  isWindowEmployee,
  scrollToBottom,
  scrollToTop,
  isManager,
} from "../../utils";
import {
  closeAuthModal,
  openAuthModal,
  openPremiumSubscriptionModal,
} from "../../utils/store/slice/appSlice";
import { downArrow } from "../../assets";
import TokenInc from "./tokenIncrement";
import Coin from "../zone/Coin";

const ProfileDialog = ({ handleLogout, setShowProfileDialog, user }) => {
  const navigate = useNavigate();

  // Determine profile route based on user type
  const isNonCustomer =
    isEmployee(user?.user_type) ||
    isAdmin(user?.user_type) ||
    isManager(user?.user_type) ||
    isWindowEmployee(user?.user_type);

  const profileRoute = isNonCustomer ? "/empprofile" : "/profile";

  useEffect(() => {
    window.addEventListener("click", () => {
      setShowProfileDialog(false);
    });
    return () => {
      window.removeEventListener("click", null);
    };
  }, []);

  return (
    <Grid
      className="profile-dialog"
      sx={{
        position: "absolute",
        top: "60px",
        right: "0px",
        backgroundColor: "white",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        minWidth: "140px",
        zIndex: 1001,
      }}
    >
      <Typography
        className="action-link"
        onClick={() => navigate(profileRoute)}
        sx={{
          padding: "12px 16px",
          cursor: "pointer",
          color: "#2474D2",
          fontWeight: "500",
          borderBottom: "1px solid #f0f0f0",
          "&:hover": {
            backgroundColor: "#f8f9ff",
            color: "#1a5bb8",
          },
          transition: "all 0.2s ease",
        }}
      >
        Profile
      </Typography>
      <Typography
        className="action-link"
        onClick={() => handleLogout()}
        sx={{
          padding: "12px 16px",
          cursor: "pointer",
          color: "#d32f2f",
          fontWeight: "500",
          "&:hover": {
            backgroundColor: "#fff5f5",
            color: "#b71c1c",
          },
          transition: "all 0.2s ease",
        }}
      >
        Logout
      </Typography>
    </Grid>
  );
};

const Navbar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLaptop = useMediaQuery(theme.breakpoints.up("lg"));
  const {
    isLoggedIn,
    userData: user,
    isPremium,
  } = useSelector((store) => store.userSlice);
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  const employee = isEmployee(user?.user_type);
  const admin = isAdmin(user?.user_type);
  const windowEmployee = isWindowEmployee(user?.user_type);
  const manager = isManager(user?.user_type);

  const openModal = () => dispatch(openAuthModal());
  const [navExpanded, setNavExpanded] = useState(false);

  const handleLogout = () => {
    dispatch(removeUser());
    localStorage.removeItem("token");
    dispatch(setLoggedIn(false));
    navigate("/");
  };

  const navButtonStyle = {
    fontWeight: "600",
    color: "#2474D2",
    height: "100%",
    position: "relative",
    textTransform: "capitalize",
    borderRadius: "8px",
    padding: "8px 16px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(36, 116, 210, 0.08)",
      transform: "translateY(-1px)",
    },
  };

  const hoverBoxStyle = {
    position: "absolute",
    top: "88px",
    display: "none",
    justifyContent: "center",
    overflowY: "auto",
    overflowX: "hidden",
    background: "linear-gradient(135deg, #2474d2 0%, #1e60a8 100%)",
    borderRadius: "0 0 12px 12px",
    boxShadow: "0 8px 32px rgba(36, 116, 210, 0.3)",
    border: "1px solid rgba(255,255,255,0.1)",
  };

  useEffect(() => {
    if (navExpanded) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [navExpanded]);

  return (
    <>
      <Grid
        sx={{
          position: "fixed",
          width: "100vw",
          top: "0px",
          zIndex: 1000,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(36, 116, 210, 0.1)",
        }}
        className="nav"
      >
        <Grid
          display="flex"
          justifyContent="space-between"
          alignItems={"center"}
          flexDirection={"row"}
          mx={{ lg: "100px", xs: "20px" }}
          height={"88px"}
        >
          <Link
            to={admin ? "/admin" : manager ? "/admin/empoyeestats" : "/"}
            onClick={() => setNavExpanded(false)}
          >
            <img
              src={Logo}
              alt="funingo-logo"
              width={"130px"}
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                transition: "transform 0.2s ease",
              }}
              onMouseOver={(e) => (e.target.style.transform = "scale(1.05)")}
              onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
            />
          </Link>

          <Grid
            className="font"
            display={{ xs: "none", lg: "flex" }}
            gap="8px"
            alignItems={"center"}
            height="100%"
          >
            {/* Manager Navigation - Only 2 items */}
            {manager && !admin && (
              <>
                <Button
                  onClick={() => {
                    navigate("/we/activity-assign");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Assign Activity
                </Button>
                <Button
                  onClick={() => {
                    navigate("/admin/userdash");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  History
                </Button>
                <Button
                  onClick={() => {
                    navigate("/blog");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Blog
                </Button>
              </>
            )}

            {/* Admin Navigation */}
            {admin && !manager && (
              <>
                <Button
                  onClick={() => {
                    navigate("/admin/ticket");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Tickets
                </Button>
                <Button
                  onClick={() => {
                    navigate("/admin/empoyeestats");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Manage
                </Button>
                <Button
                  onClick={() => navigate("/admin/bill")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Bills
                </Button>
                <Button
                  onClick={() => navigate("/admin/stats")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Statistics
                </Button>
                <Button
                  onClick={() => navigate("/admin/userdash")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  History
                </Button>
                <Button
                  onClick={() => navigate("/admin/employeecard")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  EmpCard
                </Button>
                <Button
                  onClick={() => navigate("/m/activity-update")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  ActivityUpdate
                </Button>
                <Button
                  onClick={() => {
                    navigate("/we/get-qr-tickets");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Generate QR
                </Button>
                <Button
                  onClick={() => {
                    navigate("/we/window-purchase");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Book tickets
                </Button>
              </>
            )}

            {/* Employee Navigation */}
            {employee && !admin && !manager && (
              <Button
                onClick={() => {
                  navigate("/e/redeem");
                  scrollToTop();
                }}
                sx={navButtonStyle}
                className="navText"
              >
                Redeem
              </Button>
            )}

            {/* Window Employee Navigation */}
            {windowEmployee && !admin && !manager && (
              <>
                <Button
                  onClick={() => {
                    navigate("/we/get-qr-tickets");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Generate QR
                </Button>
                <Button
                  onClick={() => {
                    navigate("/we/today-sales");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Sales Entry
                </Button>
                <Button
                  onClick={() => {
                    navigate("/admin/userdash");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  History
                </Button>
                <Button
                  onClick={() => {
                    navigate("/we/activity-assign");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Assign Activity
                </Button>

                <Button
                  onClick={() => {
                    navigate("/e/redeem");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Redeem
                </Button>
                <Button
                  onClick={() => {
                    navigate("/blog");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Blog
                </Button>
                <Button
                  onClick={() => {
                    navigate("/we/window-purchase");
                    scrollToTop();
                  }}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Book tickets
                </Button>
              </>
            )}
          </Grid>

          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isLaptop ? "16px" : "8px",
            }}
          >
            {/* Navigation buttons for regular users - placed before coin section */}
            {isLaptop && !admin && !employee && !windowEmployee && !manager && (
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginRight: "16px",
                }}
              >
                <Button
                  onClick={() => navigate("/franchise")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Franchise
                </Button>
                <Button
                  onClick={() => navigate("/gallery")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Gallery
                </Button>
                <Button
                  onClick={() => navigate("/blog")}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Blog
                </Button>

                <Button
                  onClick={() => scrollToBottom()}
                  sx={navButtonStyle}
                  className="navText"
                >
                  Contact
                </Button>
              </Grid>
            )}

            {!isLoggedIn && (
              <Button
                variant="contained"
                sx={{
                  background: isLaptop
                    ? "linear-gradient(135deg, #2474D2 0%, #1e60a8 100%)"
                    : "transparent",
                  borderRadius: "24px",
                  fontWeight: "600",
                  boxShadow: isLaptop
                    ? "0 4px 12px rgba(36, 116, 210, 0.3)"
                    : "0",
                  padding: isLaptop ? "10px 24px" : "8px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: isLaptop
                      ? "linear-gradient(135deg, #1e60a8 0%, #1a5bb8 100%)"
                      : "rgba(36, 116, 210, 0.08)",
                    boxShadow: isLaptop
                      ? "0 6px 20px rgba(36, 116, 210, 0.4)"
                      : "0",
                    transform: "translateY(-1px)",
                  },
                }}
                onClick={
                  !isLaptop
                    ? () => setNavExpanded((curr) => !curr)
                    : () => openModal()
                }
              >
                {!isLaptop ? (
                  <>
                    {!navExpanded ? (
                      <DehazeIcon sx={{ color: "#2474D2" }} />
                    ) : (
                      <ClearIcon sx={{ color: "#2474D2" }} />
                    )}
                  </>
                ) : (
                  "Get Started"
                )}
              </Button>
            )}

            {isLoggedIn && (
              <Grid
                sx={{
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {/* Only show coin display for regular users (not admin, employee, windowEmployee, or manager) */}
                {!admin && !employee && !windowEmployee && !manager && (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg, #2474D2 0%, #1e60a8 100%)",
                      borderRadius: "25px",
                      padding: "8px 20px 8px 8px",
                      minWidth: "120px",
                      height: "50px",
                      boxShadow: "0 4px 16px rgba(36, 116, 210, 0.4)",
                      border: "2px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    {/* Coin Icon Container */}
                    <Box
                      component={"img"}
                      src={coin}
                      alt="Coin Icon"
                      sx={{
                        width: "32px",
                        height: "32px",
                        marginRight: "8px",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                      }}
                    />

                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: "700",
                        fontSize: "16px",
                        textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                        letterSpacing: "0.5px",
                        lineHeight: 1,
                      }}
                    >
                      {user.funingo_money || 0}
                    </Typography>
                  </Box>
                )}

                <Box
                  component={"img"}
                  src={getProfile(user.profile_picture)}
                  sx={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    border: isPremium
                      ? "3px solid #FFD700"
                      : "3px solid #e0e0e0",
                    transition: "all 0.2s ease",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                    },
                  }}
                  onClick={
                    !isLaptop
                      ? () => setNavExpanded((curr) => !curr)
                      : (e) => {
                          e.stopPropagation();
                          setShowProfileDialog((curr) => !curr);
                        }
                  }
                />

                {showProfileDialog && (
                  <ProfileDialog
                    handleLogout={handleLogout}
                    setShowProfileDialog={setShowProfileDialog}
                    user={user}
                  />
                )}
              </Grid>
            )}
          </Grid>

          <Backdrop
            sx={{
              color: "#fff",
              zIndex: 10,
              top: "88px",
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              alignItems: "flex-start",
            }}
            open={navExpanded}
            onClick={() => setNavExpanded(false)}
            disableScrollLock={true}
          >
            <Grid
              className={
                navExpanded
                  ? "expanded-nav nav-container"
                  : "collapsed-nav nav-container"
              }
              sx={{
                background:
                  "linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)",
                borderRadius: navExpanded ? "0" : "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                display: "flex",
                flexDirection: "column",
                height: "calc(100dvh - 88px)",
                position: "fixed",
                top: "88px",
                right: 0,
                width: "100%",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                maxHeight: "calc(100dvh - 88px)",
                maxWidth: "100vw",
                transition: "transform 0.25s ease-out, opacity 0.25s ease-out",
                transform: navExpanded ? "translateX(0)" : "translateX(100%)",
                opacity: navExpanded ? 1 : 0,
                pointerEvents: navExpanded ? "auto" : "none",
                willChange: "transform, opacity",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Grid
                className="action-btns"
                sx={{
                  overflowY: "auto",
                  overflowX: "hidden",
                  flex: "1 1 auto",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  paddingBottom: "40px",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#a8a8a8",
                  },
                }}
              >
                {[
                  // Home link for customers
                  ...(!admin && !employee && !windowEmployee && !manager
                    ? [
                        {
                          label: "Home",
                          path: "/",
                          icon: "🏠",
                          gradient:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        },
                      ]
                    : []),

                  // Common items for logged in users
                  ...(isLoggedIn
                    ? [
                        {
                          label: "Profile",
                          path:
                            employee || admin || manager || windowEmployee
                              ? "/empprofile"
                              : "/profile",
                          icon: "👤",
                          gradient:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        },
                      ]
                    : []),

                  // Manager specific items - ONLY these 2
                  ...(manager && !admin
                    ? [
                        {
                          label: "History",
                          path: "/admin/userdash",
                          icon: "📋",
                          gradient:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        },
                        {
                          label: "Assign Activity",
                          path: "/we/activity-assign",
                          icon: "🎯",
                          gradient:
                            "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        },
                        {
                          label: "Blog",
                          path: "/blog",
                          icon: "📝",
                          gradient:
                            "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        },
                      ]
                    : []),

                  // Admin specific items (only if not manager)
                  ...(!manager && admin
                    ? [
                        {
                          label: "Tickets",
                          path: "/admin/ticket",
                          icon: "🎫",
                          gradient:
                            "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                        },
                        {
                          label: "Manage",
                          path: "/admin/empoyeestats",
                          icon: "⚙️",
                          gradient:
                            "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
                        },
                        {
                          label: "Bills",
                          path: "/admin/bill",
                          icon: "💰",
                          gradient:
                            "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                        },
                        {
                          label: "Statistics",
                          path: "/admin/stats",
                          icon: "📊",
                          gradient:
                            "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                        },
                        {
                          label: "History",
                          path: "/admin/userdash",
                          icon: "📋",
                          gradient:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        },
                        {
                          label: "Generate QR",
                          path: "/we/get-qr-tickets",
                          icon: "📱",
                          gradient:
                            "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)",
                        },
                        {
                          label: "EmpCard",
                          path: "/admin/employeecard",
                          icon: "💳",
                          gradient:
                            "linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)",
                        },
                        {
                          label: "ActivityUpdate",
                          path: "/m/activity-update",
                          icon: "🔄",
                          gradient:
                            "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
                        },
                      ]
                    : []),

                  // Employee specific items
                  ...(employee && !admin && !manager
                    ? [
                        {
                          label: "Redeem",
                          path: "/e/redeem",
                          icon: "🎁",
                          gradient:
                            "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                        },
                        {
                          label: "Blog",
                          path: "/blog",
                          icon: "📝",
                          gradient:
                            "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                        },
                      ]
                    : []),

                  // Window Employee specific items
                  ...(windowEmployee && !admin && !manager
                    ? [
                        {
                          label: "Generate QR",
                          path: "/we/get-qr-tickets",
                          icon: "📱",
                          gradient:
                            "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)",
                        },
                        {
                          label: "Sales Entry",
                          path: "/we/today-sales",
                          icon: "💳",
                          gradient:
                            "linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)",
                        },
                        {
                          label: "History",
                          path: "/admin/userdash",
                          icon: "📋",
                          gradient:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        },
                        {
                          label: "Assign Activity",
                          path: "/we/activity-assign",
                          icon: "🎯",
                          gradient:
                            "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        },
                        {
                          label: "Redeem",
                          path: "/e/redeem",
                          icon: "🎁",
                          gradient:
                            "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                        },
                        {
                          label: "Tickets",
                          path: "/admin/ticket",
                          icon: "🎫",
                          gradient:
                            "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                        },
                      ]
                    : []),

                  // Regular users (not admin/employee/windowEmployee/manager)
                  ...(!admin && !employee && !windowEmployee && !manager
                    ? [
                        {
                          label: "Activities",
                          path: "/zone",
                          icon: "🎮",
                          gradient:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        },
                        {
                          label: "Events",
                          path: "/events",
                          icon: "🎉",
                          gradient:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        },
                        {
                          label: "Packages",
                          path: "/packages",
                          icon: "📦",
                          gradient:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        },
                        {
                          label: "Franchise",
                          path: "/franchise",
                          icon: "🏢",
                          gradient:
                            "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        },
                        {
                          label: "Blog",
                          path: "/blog",
                          icon: "📝",
                          gradient:
                            "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        },
                        {
                          label: "Gallery",
                          path: "/gallery",
                          icon: "📸",
                          gradient:
                            "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                        },
                      ]
                    : []),
                ].map((item, index) => (
                  <Link
                    to={item.path}
                    key={index}
                    onClick={() => setNavExpanded(false)}
                  >
                    <Grid textAlign={"center"}>
                      <Button
                        sx={{
                          fontWeight: "600",
                          width: "100%",
                          color: "#2c3e50",
                          padding: "18px 20px",
                          borderBottom: "1px solid #f0f0f0",
                          borderRadius: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "16px",
                          transition: "all 0.2s ease",
                          position: "relative",
                          overflow: "hidden",
                          background: "white",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "100%",
                            width: "4px",
                            background: item.gradient,
                            transform: "scaleY(0)",
                            transition: "transform 0.2s ease",
                          },
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                            color: "#1e293b",
                            paddingLeft: "28px",
                            "&::before": {
                              transform: "scaleY(1)",
                            },
                            "& .icon-wrapper": {
                              transform: "scale(1.1)",
                            },
                          },
                          "&:active": {
                            transform: "scale(0.98)",
                          },
                        }}
                      >
                        <Box
                          className="icon-wrapper"
                          sx={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "12px",
                            background: item.gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "20px",
                            flexShrink: 0,
                            transition: "transform 0.2s ease",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: "600",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Button>
                    </Grid>
                  </Link>
                ))}

                {/* Contact/Book tickets button - only for non-managers */}
                {!manager && (
                  <Grid textAlign={"center"}>
                    <Button
                      sx={{
                        fontWeight: "600",
                        width: "100%",
                        color: "#2c3e50",
                        padding: "18px 20px",
                        borderBottom: "1px solid #f0f0f0",
                        borderRadius: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "16px",
                        transition: "all 0.2s ease",
                        position: "relative",
                        overflow: "hidden",
                        background: "white",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: "4px",
                          background: windowEmployee
                            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          transform: "scaleY(0)",
                          transition: "transform 0.2s ease",
                        },
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          color: "#1e293b",
                          paddingLeft: "28px",
                          "&::before": {
                            transform: "scaleY(1)",
                          },
                          "& .icon-wrapper": {
                            transform: "scale(1.1)",
                          },
                        },
                        "&:active": {
                          transform: "scale(0.98)",
                        },
                      }}
                      onClick={() => {
                        if (windowEmployee) {
                          navigate("/we/window-purchase");
                          scrollToTop();
                        } else {
                          scrollToBottom();
                        }
                        setNavExpanded(false);
                      }}
                    >
                      <Box
                        className="icon-wrapper"
                        sx={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "12px",
                          background: windowEmployee
                            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "20px",
                          flexShrink: 0,
                          transition: "transform 0.2s ease",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {windowEmployee ? "🎫" : "📞"}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontWeight: "600",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {windowEmployee ? "Book tickets" : "Contact"}
                      </Typography>
                    </Button>
                  </Grid>
                )}
              </Grid>

              {/* logout/login button at bottom */}
              <Grid
                sx={{
                  borderTop: "2px solid #f0f0f0",
                  backgroundColor: "#ffffff",
                  flexShrink: 0,
                  width: "100%",
                  zIndex: 20,
                  boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.05)",
                }}
              >
                <Button
                  sx={{
                    fontWeight: "700",
                    color: "white",
                    width: "100%",
                    padding: "20px",
                    borderRadius: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                    transition: "all 0.2s ease",
                    overflow: "hidden",
                    fontSize: "16px",
                    background: isLoggedIn
                      ? "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    "&:hover": {
                      background: isLoggedIn
                        ? "linear-gradient(135deg, #ee5a6f 0%, #c92a2a 100%)"
                        : "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                      transform: "translateY(-2px)",
                      "& .logout-icon": {
                        transform: "scale(1.15)",
                      },
                    },
                    "&:active": {
                      transform: "translateY(0) scale(0.98)",
                    },
                  }}
                  onClick={() => {
                    if (isLoggedIn) {
                      handleLogout();
                    } else {
                      openModal();
                    }
                    setNavExpanded(false);
                  }}
                >
                  <Box
                    className="logout-icon"
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: "rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      transition: "transform 0.2s ease",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    {isLoggedIn ? "🚪" : "🔐"}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                      textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {isLoggedIn ? "Logout" : "Get Started"}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Backdrop>
        </Grid>

        <Login />
      </Grid>
    </>
  );
};

export default Navbar;
